import React, { useState, useMemo, useEffect } from 'react';

const CalendarIcons = {
    Plus: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>,
    ChevronLeft: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
    ChevronRight: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
    Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    Edit: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
    ExternalLink: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
};

export default function PlannerGlobalCalendar({ projects, onSelectProject, updateProject }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterType, setFilterType] = useState('all'); // 'all', 'events', 'tasks'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'agenda'
  const [selectedDateView, setSelectedDateView] = useState(null); 

  const pad = (n) => n.toString().padStart(2, '0');
  const todayStr = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;

  // Modal State for Adding/Editing
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
      projectId: '',
      type: 'task', 
      title: '',
      date: todayStr,
      time: '12:00',
      location: '', 
      notes: '',
      editId: null 
  });

  // Auto-select first project in dropdown if none selected
  useEffect(() => {
      if (!addFormData.projectId && projects.length > 0) {
          setAddFormData(prev => ({ ...prev, projectId: projects[0].id }));
      }
  }, [projects, addFormData.projectId]);

  /**
   * Handle Create/Update Logic
   */
  const handleAddItem = (e) => {
      e.preventDefault();
      if (!addFormData.projectId || !addFormData.title) return;

      const targetProject = projects.find(p => p.id === addFormData.projectId);
      if (!targetProject) return;

      if (addFormData.editId) {
          // UPDATE
          if (addFormData.type === 'task') {
              const newTasks = (targetProject.tasks || []).map(t => 
                  t.id === addFormData.editId ? { ...t, text: addFormData.title, dueDate: addFormData.date, location: addFormData.location || '' } : t
              );
              updateProject(targetProject.id, { tasks: newTasks });
          } else if (addFormData.type === 'timeline') {
              const newTimeline = (targetProject.timeline || []).map(t => 
                  t.id === addFormData.editId ? { ...t, event: addFormData.title, date: addFormData.date, time: addFormData.time || '12:00', location: addFormData.location || '', notes: addFormData.notes || '' } : t
              ).sort((a,b) => (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || ''));
              updateProject(targetProject.id, { timeline: newTimeline });
          } else if (addFormData.type === 'note') {
              const newNotes = (targetProject.notes || []).map(n => 
                  n.id === addFormData.editId ? { ...n, title: addFormData.title, content: addFormData.notes || '', date: addFormData.date, location: addFormData.location || '' } : n
              );
              updateProject(targetProject.id, { notes: newNotes });
          }
      } else {
          // CREATE
          if (addFormData.type === 'task') {
              const newTask = {
                  id: Date.now().toString(),
                  text: addFormData.title,
                  category: 'General',
                  priority: 'Medium',
                  completed: false,
                  dueDate: addFormData.date,
                  location: addFormData.location || ''
              };
              updateProject(targetProject.id, { tasks: [...(targetProject.tasks || []), newTask] });
          } else if (addFormData.type === 'timeline') {
              const newEvent = {
                  id: Date.now().toString(),
                  event: addFormData.title,
                  date: addFormData.date,
                  time: addFormData.time || '12:00',
                  category: 'Other',
                  assigned: '',
                  notes: addFormData.notes || '',
                  location: addFormData.location || ''
              };
              updateProject(targetProject.id, { 
                  timeline: [...(targetProject.timeline || []), newEvent].sort((a,b) => (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || '')) 
              });
          } else if (addFormData.type === 'note') {
              const newNote = {
                  id: Date.now().toString(),
                  title: addFormData.title,
                  content: addFormData.notes || '',
                  date: addFormData.date,
                  location: addFormData.location || ''
              };
              updateProject(targetProject.id, { notes: [newNote, ...(targetProject.notes || [])] });
          }
      }
      
      setShowAddModal(false);
      setAddFormData(prev => ({ ...prev, title: '', notes: '', location: '', editId: null }));
  };

  const handleDeleteItem = (item) => {
      if (!window.confirm(`Are you sure you want to delete this ${item.type}?`)) return;
      const targetProject = item.project;
      
      if (item.type === 'task' || item.type === 'reminder') {
          const realId = item.type === 'reminder' ? item.id.replace('rem-', '') : item.id;
          updateProject(targetProject.id, { tasks: targetProject.tasks.filter(t => t.id !== realId) });
      } else if (item.type === 'timeline') {
          updateProject(targetProject.id, { timeline: targetProject.timeline.filter(t => t.id !== item.id) });
      } else if (item.type === 'note') {
          updateProject(targetProject.id, { notes: targetProject.notes.filter(n => n.id !== item.id) });
      }
  };

  const handleEditItem = (item, specificDate) => {
      if (item.type === 'event') {
          onSelectProject(item.project.id);
          return;
      }
      
      const realId = item.type === 'reminder' ? item.id.replace('rem-', '') : item.id;
      const realType = item.type === 'reminder' ? 'task' : item.type;
      
      let notesVal = '';
      let timeVal = '12:00';
      let locationVal = item.location || '';
      
      if (realType === 'timeline') {
          const t = item.project.timeline?.find(x => x.id === realId);
          if (t) { notesVal = t.notes || ''; timeVal = t.time || '12:00'; locationVal = t.location || ''; }
      } else if (realType === 'note') {
          const n = item.project.notes?.find(x => x.id === realId);
          if (n) { notesVal = n.content || ''; locationVal = n.location || ''; }
      } else if (realType === 'task') {
          const t = item.project.tasks?.find(x => x.id === realId);
          if (t) { locationVal = t.location || ''; }
      }
      
      setAddFormData({
          projectId: item.project.id,
          type: realType,
          title: item.title,
          date: specificDate,
          time: timeVal,
          notes: notesVal,
          location: locationVal,
          editId: realId
      });
      
      setSelectedDateView(null);
      setShowAddModal(true);
  };

  /**
   * Data Aggregation Logic
   */
  const calendarItems = useMemo(() => {
      const items = {};
      projects.forEach(p => {
          // 1. Wedding Days
          if (p.date && p.date !== 'TBD') {
              if (!items[p.date]) items[p.date] = [];
              items[p.date].push({ type: 'event', id: p.id, title: p.clientNames, project: p, location: p.location });
          }
          // 2. Tasks
          (p.tasks || []).forEach(t => {
              if (t.dueDate) {
                  if (!items[t.dueDate]) items[t.dueDate] = [];
                  items[t.dueDate].push({ type: 'task', id: t.id, title: t.text, project: p, completed: t.completed, location: t.location });
              }
              if (t.reminderDate && t.reminderDate !== t.dueDate) {
                  if (!items[t.reminderDate]) items[t.reminderDate] = [];
                  items[t.reminderDate].push({ type: 'reminder', id: `rem-${t.id}`, title: `Reminder: ${t.text}`, project: p, completed: t.completed, location: t.location });
              }
          });
          // 3. Timeline specifics
          (p.timeline || []).forEach(tl => {
              if (tl.date && tl.date !== p.date) {
                  if (!items[tl.date]) items[tl.date] = [];
                  items[tl.date].push({ type: 'timeline', id: tl.id, title: tl.event, time: tl.time, project: p, location: tl.location });
              }
          });
          // 4. Notes
          (p.notes || []).forEach(n => {
              const noteDateStr = n.date ? n.date.split('T')[0] : null;
              if (noteDateStr) {
                  if (!items[noteDateStr]) items[noteDateStr] = [];
                  items[noteDateStr].push({ type: 'note', id: n.id, title: n.title, project: p, location: n.location });
              }
          });
      });
      return items;
  }, [projects]);

  const upcomingItems = useMemo(() => {
      const today = new Date();
      today.setHours(0,0,0,0);
      const allItems = [];
      
      Object.entries(calendarItems).forEach(([dateStr, items]) => {
          const [y, m, d] = dateStr.split('-');
          const itemDate = new Date(y, m - 1, d);
          if (itemDate >= today) {
              items.forEach(item => {
                  if (filterType === 'all' || 
                      (filterType === 'events' && (item.type === 'timeline' || item.type === 'event')) || 
                      (filterType === 'tasks' && (item.type === 'task' || item.type === 'reminder')) ||
                      filterType === item.type + 's') {
                      allItems.push({ ...item, date: dateStr, dateObj: itemDate });
                  }
              });
          }
      });
      
      return allItems.sort((a, b) => a.dateObj - b.dateObj).slice(0, 30);
  }, [calendarItems, filterType]);

  const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const handleToday = () => setCurrentMonth(new Date());

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return (
    <div className="p-4 md:p-8 animate-fade-in h-full flex flex-col relative bg-gray-50">
      
      {/* Daily Detail Modal */}
      {selectedDateView && (
          <div className="fixed inset-0 z-[350] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedDateView(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh] overflow-hidden border border-gray-100" onClick={e => e.stopPropagation()}>
                  <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-white flex-shrink-0">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <span className="text-2xl">📅</span>
                          {new Date(selectedDateView.split('-')[0], selectedDateView.split('-')[1] - 1, selectedDateView.split('-')[2]).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </h3>
                      <div className="flex items-center gap-2">
                          <button 
                              onClick={() => { setAddFormData(prev => ({...prev, date: selectedDateView, type: 'task', editId: null})); setShowAddModal(true); }}
                              className="text-xs bg-pink-600 text-white hover:bg-pink-700 px-3 py-1.5 rounded-lg font-bold transition shadow-md flex items-center gap-1.5"
                          >
                              <CalendarIcons.Plus /> Add Item
                          </button>
                          <button onClick={() => setSelectedDateView(null)} className="text-gray-400 hover:text-red-500 text-2xl leading-none bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-sm border border-gray-200">&times;</button>
                      </div>
                  </div>
                  <div className="p-5 overflow-y-auto flex-1 space-y-4 custom-scrollbar bg-gray-50/50">
                      {calendarItems[selectedDateView] && calendarItems[selectedDateView].length > 0 ? (
                          calendarItems[selectedDateView].map((item, idx) => (
                              <div key={idx} className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm flex flex-col gap-3 group hover:border-pink-300 transition-all">
                                  <div className="flex justify-between items-start">
                                      <div className="flex items-center gap-2 flex-wrap">
                                           <span className={`text-[9px] px-2 py-0.5 rounded-md font-black uppercase tracking-widest ${item.type === 'event' ? 'bg-pink-100 text-pink-700' : item.type === 'timeline' ? 'bg-purple-100 text-purple-700' : item.type === 'note' ? 'bg-yellow-100 text-yellow-700' : item.type === 'reminder' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                               {item.type}
                                           </span>
                                           {item.time && <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">⏱️ {item.time}</span>}
                                      </div>
                                      <button onClick={() => onSelectProject(item.project.id)} className="text-gray-400 hover:text-pink-600 text-[10px] font-black uppercase tracking-tighter bg-gray-50 hover:bg-pink-50 px-2 py-1 rounded transition flex items-center gap-1 border border-gray-100 shadow-xs">
                                          Workspace <CalendarIcons.ChevronRight />
                                      </button>
                                  </div>
                                  <div>
                                      <p className={`font-bold text-gray-900 text-lg leading-tight ${item.completed ? 'line-through text-gray-400' : ''}`}>{item.title}</p>
                                      <p className="text-xs text-gray-500 font-medium mt-1 uppercase tracking-wide">Client: {item.project.clientNames}</p>
                                      {item.location && (
                                          <p className="text-xs text-blue-600 font-medium mt-2 flex items-start gap-1">
                                              <span>📍</span>
                                              <span className="hover:underline cursor-pointer">{item.location}</span>
                                          </p>
                                      )}
                                  </div>
                                  
                                  {item.type !== 'event' && (
                                      <div className="flex gap-2 justify-end pt-3 border-t border-gray-50 mt-1">
                                          <button onClick={() => handleEditItem(item, selectedDateView)} className="text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition shadow-sm flex items-center gap-1.5"><CalendarIcons.Edit /> Edit</button>
                                          <button onClick={() => handleDeleteItem(item)} className="text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg text-xs font-bold transition shadow-sm flex items-center gap-1.5"><CalendarIcons.Trash /> Delete</button>
                                      </div>
                                  )}
                              </div>
                          ))
                      ) : (
                          <div className="text-center py-20 flex flex-col items-center bg-white rounded-2xl border-2 border-dashed border-gray-200">
                              <span className="text-5xl mb-4 opacity-50">📅</span>
                              <p className="text-gray-500 font-bold">No tasks or events for this date.</p>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* Universal Add/Edit Modal */}
      {showAddModal && (
          <div className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[95vh] overflow-y-auto custom-scrollbar border border-gray-100">
                  <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                          <span className="text-pink-600 bg-pink-50 w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-inner">{addFormData.editId ? '✏️' : '✨'}</span>
                          {addFormData.editId ? 'Edit Item' : 'New Reminder / Event'}
                      </h3>
                      <button onClick={() => { setShowAddModal(false); setAddFormData(prev => ({...prev, editId: null, title: '', notes: '', location: ''})); }} className="text-gray-400 hover:text-red-500 transition text-2xl leading-none">&times;</button>
                  </div>
                  
                  {projects.length === 0 ? (
                      <div className="text-center py-6">
                          <p className="text-gray-600 font-medium mb-4">You need an active project to add items to the calendar.</p>
                          <button onClick={() => setShowAddModal(false)} className="bg-gray-100 px-6 py-2.5 rounded-xl font-bold text-gray-700 hover:bg-gray-200 transition">Close</button>
                      </div>
                  ) : (
                      <form onSubmit={handleAddItem} className="space-y-5">
                          <div>
                              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Target Client Project</label>
                              <select 
                                  value={addFormData.projectId} 
                                  onChange={e => setAddFormData({...addFormData, projectId: e.target.value})}
                                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 outline-none focus:ring-2 focus:ring-pink-500 transition-all appearance-none"
                                  required
                                  disabled={!!addFormData.editId}
                              >
                                  <option value="" disabled>Select project...</option>
                                  {projects.map(p => <option key={p.id} value={p.id}>{p.clientNames}</option>)}
                              </select>
                          </div>
                          
                          {!addFormData.editId && (
                              <div>
                                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">What kind of item?</label>
                                  <div className="grid grid-cols-3 gap-2">
                                      {['task', 'timeline', 'note'].map(type => (
                                          <label key={type} className={`cursor-pointer text-center p-3 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${addFormData.type === type ? 'bg-pink-600 border-pink-600 text-white shadow-lg shadow-pink-200' : 'bg-white border-gray-100 hover:border-pink-200 text-gray-500'}`}>
                                              <input type="radio" name="itemType" value={type} checked={addFormData.type === type} onChange={e => setAddFormData({...addFormData, type: e.target.value})} className="hidden" />
                                              {type === 'timeline' ? 'Event' : type}
                                          </label>
                                      ))}
                                  </div>
                              </div>
                          )}

                          <div>
                              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Subject / Description</label>
                              <input 
                                  type="text" 
                                  value={addFormData.title} 
                                  onChange={e => setAddFormData({...addFormData, title: e.target.value})}
                                  placeholder={addFormData.type === 'note' ? "e.g. Venue Walkthrough Notes" : "e.g. Pick up linens"}
                                  className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold outline-none focus:bg-white focus:ring-2 focus:ring-pink-500 transition-all shadow-inner"
                                  required
                                  autoFocus
                              />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Date</label>
                                  <input 
                                      type="date" 
                                      value={addFormData.date} 
                                      onChange={e => setAddFormData({...addFormData, date: e.target.value})}
                                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold outline-none focus:bg-white transition-all shadow-inner"
                                      required
                                  />
                              </div>
                              {addFormData.type === 'timeline' && (
                                  <div>
                                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Time</label>
                                      <input 
                                          type="time" 
                                          value={addFormData.time} 
                                          onChange={e => setAddFormData({...addFormData, time: e.target.value})}
                                          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold outline-none focus:bg-white transition-all shadow-inner"
                                          required
                                      />
                                  </div>
                              )}
                          </div>

                          <div>
                              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Location (Optional)</label>
                              <input 
                                  type="text" 
                                  value={addFormData.location} 
                                  onChange={e => setAddFormData({...addFormData, location: e.target.value})}
                                  placeholder="Address or venue name..."
                                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:bg-white transition-all shadow-inner"
                              />
                          </div>

                          {(addFormData.type === 'note' || addFormData.type === 'timeline') && (
                              <div>
                                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 ml-1">Internal Details</label>
                                  <textarea 
                                      value={addFormData.notes} 
                                      onChange={e => setAddFormData({...addFormData, notes: e.target.value})}
                                      placeholder="Private notes for your team..."
                                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium outline-none focus:bg-white focus:ring-2 focus:ring-pink-500 h-28 resize-none shadow-inner transition-all"
                                  />
                              </div>
                          )}

                          <div className="pt-6 border-t border-gray-100 flex justify-end gap-3">
                              <button type="button" onClick={() => { setShowAddModal(false); setAddFormData(prev => ({...prev, editId: null, title: '', notes: '', location: ''})); }} className="px-6 py-2.5 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition text-sm">Cancel</button>
                              <button type="submit" className="px-8 py-3 bg-gray-900 text-white rounded-xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95">{addFormData.editId ? 'Update Entry' : 'Add to Calendar'}</button>
                          </div>
                      </form>
                  )}
              </div>
          </div>
      )}

      <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden relative">
         
         {/* Top Header & Navigation */}
         <div className="p-6 border-b border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 bg-white z-20 shadow-xs">
            <div>
               <h2 className="text-3xl font-bold text-gray-900 font-serif">Master Calendar</h2>
               <p className="text-sm text-gray-500 mt-1 font-medium italic">Your unified command center for all events and deadlines.</p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap items-center gap-4 w-full xl:w-auto">
               <button onClick={() => { setAddFormData(prev => ({...prev, date: todayStr, type: 'task', editId: null})); setShowAddModal(true); }} className="bg-pink-600 text-white px-6 py-3 rounded-2xl font-black text-sm hover:bg-pink-700 transition shadow-lg shadow-pink-500/20 flex items-center justify-center gap-2 transform active:scale-95 w-full sm:w-auto">
                   <CalendarIcons.Plus /> New Reminder
               </button>
               
               <div className="flex items-center bg-gray-100 p-1.5 rounded-2xl border border-gray-200 shadow-inner w-full sm:w-auto overflow-hidden">
                    <button onClick={() => setViewMode('grid')} className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${viewMode === 'grid' ? 'bg-white text-pink-600 shadow-md' : 'text-gray-400 hover:text-gray-900'}`}>Grid</button>
                    <button onClick={() => setViewMode('agenda')} className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${viewMode === 'agenda' ? 'bg-white text-pink-600 shadow-md' : 'text-gray-400 hover:text-gray-900'}`}>Agenda</button>
               </div>

               {viewMode === 'grid' && (
                   <div className="flex items-center gap-3 w-full sm:w-auto">
                       <button onClick={handleToday} className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition transform active:scale-95">Today</button>
                       <div className="flex items-center bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1 sm:flex-none">
                          <button onClick={handlePrevMonth} className="p-3 text-gray-500 hover:bg-gray-50 transition border-r border-gray-100"><CalendarIcons.ChevronLeft /></button>
                          <span className="font-black text-gray-900 min-w-[150px] text-center text-sm uppercase tracking-widest">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                          <button onClick={handleNextMonth} className="p-3 text-gray-500 hover:bg-gray-50 transition border-l border-gray-100"><CalendarIcons.ChevronRight /></button>
                       </div>
                   </div>
               )}
            </div>
         </div>

         {/* View Container */}
         <div className="flex-1 flex flex-col overflow-hidden bg-gray-50/30">
            {viewMode === 'agenda' ? (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
                    <div className="max-w-4xl mx-auto space-y-6">
                        <div className="flex items-center gap-4 mb-8">
                            <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-lg text-xs font-black uppercase tracking-widest border border-pink-200">Timeline</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent"></div>
                            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-white border border-gray-200 text-xs font-bold rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-pink-500 shadow-sm">
                                <option value="all">All Deliverables</option>
                                <option value="events">Weddings & Timeline</option>
                                <option value="tasks">Tasks & Reminders</option>
                                <option value="notes">Notes Only</option>
                            </select>
                        </div>
                        
                        {upcomingItems.length > 0 ? upcomingItems.map((item, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-xl hover:border-pink-200 transition-all group animate-fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                                <div className="flex items-center gap-6 min-w-0 flex-1 cursor-pointer" onClick={() => onSelectProject(item.project.id)}>
                                    <div className="w-16 h-16 rounded-[1.5rem] bg-gray-900 border border-gray-950 flex flex-col items-center justify-center flex-shrink-0 transition-colors group-hover:bg-pink-600 group-hover:border-pink-500 shadow-lg text-white">
                                        <span className="text-[10px] font-black uppercase opacity-60 tracking-tighter">{item.dateObj.toLocaleString('default', { month: 'short' })}</span>
                                        <span className="text-2xl font-black">{item.dateObj.getDate()}</span>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            <span className={`text-[8px] font-black uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${item.type === 'event' ? 'bg-pink-100 text-pink-700' : item.type === 'timeline' ? 'bg-purple-100 text-purple-700' : item.type === 'note' ? 'bg-yellow-100 text-yellow-700' : item.type === 'reminder' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {item.type}
                                            </span>
                                            {(item.type === 'task' || item.type === 'reminder') && item.completed && <span className="text-[8px] bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full font-black tracking-widest uppercase">DONE</span>}
                                            {item.type === 'timeline' && <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1"><span className="text-lg leading-none">⏱️</span> {item.time}</span>}
                                        </div>
                                        <p className={`font-serif font-bold text-xl leading-tight group-hover:text-pink-700 transition-colors ${(item.type === 'task' || item.type === 'reminder') && item.completed ? 'line-through text-gray-300' : 'text-gray-900'}`}>{item.title}</p>
                                        <p className="text-[11px] text-gray-400 font-bold uppercase mt-1.5 tracking-wide flex items-center gap-2">
                                            Client: <span className="text-gray-800">{item.project.clientNames}</span>
                                        </p>
                                        {item.location && (
                                            <p className="text-xs text-blue-500 font-bold mt-2 flex items-center gap-1.5">
                                                <span className="text-base leading-none">📍</span> {item.location}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6">
                                    {item.type !== 'event' && (
                                        <div className="flex gap-2 mr-2">
                                            <button onClick={() => handleEditItem(item, item.date)} className="p-3 text-blue-500 bg-blue-50 hover:bg-blue-600 hover:text-white rounded-2xl shadow-sm transition-all transform active:scale-90" title="Edit Entry"><CalendarIcons.Edit /></button>
                                            <button onClick={() => handleDeleteItem(item)} className="p-3 text-red-500 bg-red-50 hover:bg-red-600 hover:text-white rounded-2xl shadow-sm transition-all transform active:scale-90" title="Delete Entry"><CalendarIcons.Trash /></button>
                                        </div>
                                    )}
                                    <button onClick={() => onSelectProject(item.project.id)} className="group/btn bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-[1.2rem] text-sm font-black transition-all shadow-xl shadow-gray-900/10 flex items-center gap-2 transform active:scale-95">
                                        Open <CalendarIcons.ChevronRight />
                                    </button>
                                </div>
                            </div>
                        )) : (
                            <div className="text-center py-32 bg-white rounded-[2.5rem] border border-gray-100 shadow-inner">
                                <div className="text-6xl mb-6">🏝️</div>
                                <h4 className="text-xl font-bold text-gray-900 mb-2">The schedule is clear!</h4>
                                <p className="text-gray-500 max-w-xs mx-auto">No upcoming items match your current filters. Take a break or add a new reminder.</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex flex-col overflow-auto custom-scrollbar p-3 md:p-6">
                    <div className="min-w-[1000px] flex flex-col h-full bg-gray-200 gap-px border border-gray-200 rounded-2xl overflow-hidden shadow-2xl">
                        {/* Days of Week Row */}
                        <div className="grid grid-cols-7 gap-px flex-shrink-0 bg-white border-b border-gray-200">
                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d) => (
                                <div key={d} className="font-black text-[10px] text-gray-400 uppercase tracking-[0.2em] py-4 text-center">
                                    {d}
                                </div>
                            ))}
                        </div>
                        {/* Calendar Grid Cells */}
                        <div className="grid grid-cols-7 gap-px flex-1">
                            {/* Empty cells before month start */}
                            {Array.from({ length: firstDay }).map((_, i) => (
                                <div key={`empty-${i}`} className="bg-gray-50/50"></div>
                            ))}
                            {/* Day cells */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                                const day = i + 1;
                                const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
                                const isToday = dateStr === todayStr;
                                const items = calendarItems[dateStr] || [];
                                
                                return (
                                    <div 
                                        key={day} 
                                        onClick={() => setSelectedDateView(dateStr)} 
                                        className={`bg-white p-2.5 flex flex-col gap-2 transition-all hover:bg-pink-50/20 group cursor-pointer relative ${isToday ? 'z-10' : ''}`}
                                    >
                                        <div className="flex justify-between items-center mb-1">
                                            <span className={`text-xs font-black inline-flex items-center justify-center w-7 h-7 rounded-xl transition-all ${isToday ? 'bg-pink-600 text-white shadow-lg shadow-pink-500/30' : 'text-gray-400 group-hover:text-gray-900 group-hover:bg-gray-100'}`}>{day}</span>
                                            {items.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse"></span>}
                                        </div>
                                        <div className="flex-1 overflow-y-auto space-y-1.5 max-h-[120px] pr-1 custom-scrollbar pointer-events-none">
                                            {items.map((e, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`text-[9px] font-black px-2 py-1.5 rounded-lg truncate shadow-xs border transition-transform ${
                                                        e.type === 'event' ? 'bg-gray-900 text-white border-black ring-1 ring-white/10' : 
                                                        e.type === 'timeline' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                                        e.type === 'note' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                                        e.type === 'reminder' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                                        'bg-blue-100 text-blue-800 border-blue-200'
                                                    }`}
                                                >
                                                    {e.type === 'event' ? '💍 ' : e.type === 'timeline' ? '⏱️ ' : ''}{e.title}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Hover Overlay Visual */}
                                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 border-2 border-pink-500/20 rounded-lg pointer-events-none transition-opacity"></div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
         </div>
      </div>
    </div>
  );
}